import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLang } from "@/i18n/LanguageContext";
import { useAuth } from "@/lib/AuthContext";
import { fetchList } from "@/lib/api";
import { Mail, Search, Menu, X, User, LogIn, LogOut, ShieldCheck, UserPlus, ChevronRight, ArrowRight } from "lucide-react";
import { CORPORATE_NAV, CAPABILITIES } from "@/lib/dataleo";
import ClientLogoBadge from "@/components/ClientLogoBadge";

const PARTNER_CAT_LABELS = {
  technology_partner:      { en: "Technology",     fr: "Technologie" },
  consulting_partner:      { en: "Consulting",     fr: "Conseil" },
  system_integrator:       { en: "Integrators",    fr: "Intégrateurs" },
  data_digital_specialist: { en: "Data / digital", fr: "Data / digital" },
  independent_expert:      { en: "Independent experts", fr: "Experts indépendants" },
  research_academic:       { en: "Research",       fr: "Recherche" },
};

const FlagFR = ({ active }) => (
  <svg width="20" height="14" viewBox="0 0 22 16" className={`block rounded-[2px] border ${active ? "border-[#0B1210]" : "border-transparent"}`} aria-hidden>
    <rect width="7.33" height="16" fill="#002395" />
    <rect x="7.33" width="7.33" height="16" fill="#fff" />
    <rect x="14.66" width="7.33" height="16" fill="#ED2939" />
  </svg>
);
const FlagGB = ({ active }) => (
  <svg width="20" height="14" viewBox="0 0 60 30" className={`block rounded-[2px] border ${active ? "border-[#0B1210]" : "border-transparent"}`} aria-hidden>
    <clipPath id="t"><path d="M30,15h30v15zv15h-30zh-30v-15zv-15h30z" /></clipPath>
    <path d="M0,0v30h60v-30z" fill="#012169" />
    <path d="M0,0 60,30M60,0 0,30" stroke="#fff" strokeWidth="6" />
    <path d="M0,0 60,30M60,0 0,30" clipPath="url(#t)" stroke="#C8102E" strokeWidth="4" />
    <path d="M30,0v30M0,15h60" stroke="#fff" strokeWidth="10" />
    <path d="M30,0v30M0,15h60" stroke="#C8102E" strokeWidth="6" />
  </svg>
);

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ai-ops-signal/artifacts/ocf5prlb_dataleo_large.png";

const Header = () => {
  const { lang, setLang, langRoutes } = useLang();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawer, setDrawer] = useState(false);
  const [userMenu, setUserMenu] = useState(false);
  const userMenuRef = useRef(null);

  const path = location.pathname;
  // On an article, switching language opens its translated record when one exists.
  const switchLang = (l) => {
    setLang(l);
    const target = langRoutes?.[l];
    if (target && target !== path) navigate(target);
  };
  const scope =
    path.startsWith("/radar") ? "radar"
    : path.startsWith("/labs") ? "labs"
    : path.startsWith("/capabilities") ? "capabilities"
    : path.startsWith("/clients") ? "clients"
    : path.startsWith("/partners") ? "partners"
    : "corporate";

  useEffect(() => { setDrawer(false); setUserMenu(false); }, [path]);
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.body.style.overflow = drawer ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [drawer]);
  useEffect(() => {
    if (!userMenu) return;
    const onDoc = (e) => { if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenu(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [userMenu]);

  const doLogout = async () => { await logout(); setUserMenu(false); navigate("/"); };

  const radarNav = [
    { to: "/radar", label: lang === "fr" ? "Accueil" : "Home", end: true },
    { to: "/radar/news", label: "News" },
    { to: "/radar/jobs", label: "Jobs" },
    { to: "/radar/alerts", label: lang === "fr" ? "Alertes" : "Alerts" },
    { to: "/radar/insights", label: "Insights" },
    { to: "/radar/tutorials", label: "Tutorials" },
    { to: "/radar/ecosystem", label: lang === "fr" ? "Écosystème" : "Ecosystem" },
  ];

  const [labsProjects, setLabsProjects] = useState([]);
  useEffect(() => {
    if (scope !== "labs") return;
    let cancelled = false;
    fetchList("/labs", { language: lang, limit: 60 })
      .then((rows) => { if (!cancelled) setLabsProjects(rows || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [scope, lang]);

  const labsNav = [
    { to: "/labs", label: lang === "fr" ? "Tous les projets" : "All projects", end: true },
    ...labsProjects.map((p) => ({ to: `/labs/${p.slug}`, label: p.title })),
  ];

  const capabilitiesNav = useMemo(() => [
    { to: "/capabilities", label: lang === "fr" ? "Toutes les capacités" : "All capabilities", end: true },
    ...CAPABILITIES.map((c) => ({ to: `/capabilities/${c.slug}`, label: c.title[lang] || c.title.en })),
  ], [lang]);

  const [clients, setClients] = useState([]);
  useEffect(() => {
    if (scope !== "clients") return;
    let cancelled = false;
    fetchList("/clients", { language: lang, limit: 30 })
      .then((rows) => {
        if (cancelled) return;
        const sorted = [...(rows || [])].sort((a, b) => {
          if (!!b.featured !== !!a.featured) return (b.featured ? 1 : 0) - (a.featured ? 1 : 0);
          return (a.display_order ?? 0) - (b.display_order ?? 0);
        });
        setClients(sorted);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [scope, lang]);

  const clientsNav = [
    { to: "/clients", label: lang === "fr" ? "Toutes les références" : "All references", end: true },
    ...clients
      .filter((c) => (c.slug || c.id) && (c.client_name || c.anonymous_descriptor))
      .slice(0, 8)
      .map((c) => ({
        to: `/clients/${c.slug || c.id}`,
        label: c.client_name || c.anonymous_descriptor,
        leadingIcon: <ClientLogoBadge client={c} size={16} />,
      })),
  ];

  const [partners, setPartners] = useState([]);
  useEffect(() => {
    if (scope !== "partners") return;
    let cancelled = false;
    fetchList("/partners", { language: lang, limit: 100 })
      .then((rows) => { if (!cancelled) setPartners(rows || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [scope, lang]);

  const partnersNav = useMemo(() => {
    const activeCats = new Set(partners.map((p) => p.category).filter(Boolean));
    return [
      { to: "/partners", label: lang === "fr" ? "Tous les partenaires" : "All partners", end: true },
      ...Object.keys(PARTNER_CAT_LABELS)
        .filter((c) => activeCats.has(c))
        .map((c) => ({ to: `/partners#${c}`, label: PARTNER_CAT_LABELS[c][lang] || PARTNER_CAT_LABELS[c].en, hash: true })),
    ];
  }, [partners, lang]);

  const corporateNav = CORPORATE_NAV.map((n) => ({
    to: n.to,
    label: lang === "fr" ? n.fr : n.en,
    end: n.exact,
  }));

  const subNavByScope = {
    radar:        { items: radarNav,        label: "Radar" },
    labs:         { items: labsNav,         label: "Labs" },
    capabilities: { items: capabilitiesNav, label: lang === "fr" ? "Capacités" : "Capabilities" },
    clients:      { items: clientsNav,      label: lang === "fr" ? "Clients"   : "Clients" },
    partners:     { items: partnersNav,     label: lang === "fr" ? "Partenaires" : "Partners" },
  };
  const subNav = subNavByScope[scope] || null;

  const drawerSubLabelByScope = {
    radar: "- Radar -",
    labs: "- Labs -",
    capabilities: lang === "fr" ? "- Capacités -" : "- Capabilities -",
    clients: "- Clients -",
    partners: lang === "fr" ? "- Partenaires -" : "- Partners -",
  };
  const drawerSubLabel = drawerSubLabelByScope[scope];
  const drawerSubItems = subNav?.items || [];
  const drawerNav = subNav ? [
    ...corporateNav,
    { to: subNav.items[0].to, label: drawerSubLabel, divider: true, headerOnly: true },
    ...drawerSubItems,
  ] : corporateNav;

  return (
    <>
      {/* Header - Citwell-inspired: white/cream background, bold typography,
          pill CTA, generous letter spacing. */}
      <header data-testid="site-header" className="sticky top-0 z-50 bg-[#F6F3EE]/95 backdrop-blur-md border-b border-[#E6E0D5]">
        <div className="dl-container flex items-center justify-between h-[72px] gap-4 sm:gap-8">
          <Link to="/" data-testid="brand-link" className="flex items-center gap-2 group shrink min-w-0">
            <img
              src={LOGO_URL}
              alt="Dataleo"
              className="h-auto w-[150px] sm:w-[220px] lg:h-8 lg:w-auto max-w-full"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          </Link>

          {/* Corporate nav - centered, bold, uppercase-style weight */}
          <nav data-testid="primary-nav" className="hidden md:flex items-center gap-7 flex-1 justify-center">
            {corporateNav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`nav-${n.to.replace(/\//g, "-").replace(/^-/, "")}`}
                className={({ isActive }) =>
                  `relative text-[14px] font-bold-dl whitespace-nowrap transition-colors ${
                    isActive ? "text-[#00A878]" : "text-[#0B1210] hover:text-[#00A878]"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <button
              data-testid="search-icon-btn"
              onClick={() => navigate("/radar/search")}
              aria-label={lang === "fr" ? "Rechercher dans le Radar" : "Search Radar"}
              className="hidden sm:grid place-items-center w-10 h-10 rounded-full text-[#0B1210] hover:bg-[#EFEAE0] transition-colors"
            >
              <Search size={17} />
            </button>

            <div data-testid="lang-switcher" className="hidden sm:flex items-center gap-1">
              <button
                data-testid="lang-fr"
                onClick={() => switchLang("fr")}
                aria-label="Français"
                className={`p-1.5 rounded-full transition-opacity ${lang === "fr" ? "opacity-100" : "opacity-45 hover:opacity-90"}`}
              >
                <FlagFR active={lang === "fr"} />
              </button>
              <button
                data-testid="lang-en"
                onClick={() => switchLang("en")}
                aria-label="English"
                className={`p-1.5 rounded-full transition-opacity ${lang === "en" ? "opacity-100" : "opacity-45 hover:opacity-90"}`}
              >
                <FlagGB active={lang === "en"} />
              </button>
            </div>

            {user ? (
              <div ref={userMenuRef} className="relative hidden sm:block">
                <button
                  data-testid="user-menu-btn"
                  onClick={() => setUserMenu((v) => !v)}
                  aria-label="Open user menu"
                  className="inline-flex items-center gap-2 px-2 h-10 rounded-full text-[13px] font-bold-dl text-[#0B1210] hover:bg-[#EFEAE0] max-w-[160px]"
                >
                  <span className="w-7 h-7 rounded-full bg-[#00A878] text-white grid place-items-center text-[11px] font-bold shrink-0">
                    {(user.name || user.email || "?").slice(0, 1).toUpperCase()}
                  </span>
                  <span className="truncate">{user.name || user.email.split("@")[0]}</span>
                </button>
                {userMenu && (
                  <div
                    data-testid="user-menu-dropdown"
                    className="absolute right-0 top-full mt-2 w-60 bg-white text-[#0B1210] border border-[#E6E0D5] rounded-2xl shadow-xl z-50 overflow-hidden"
                  >
                    <div className="px-4 py-3 border-b border-[#E6E0D5] text-[11px] text-[#4B5563] truncate">{user.email}</div>
                    <Link to="/account" data-testid="user-menu-account" onClick={() => setUserMenu(false)} className="flex items-center gap-2 px-4 py-3 text-[13px] font-bold-dl hover:bg-[#F6F3EE]">
                      <User size={14} /> {lang === "fr" ? "Mon compte" : "My account"}
                    </Link>
                    {user.role === "admin" && (
                      <Link to="/admin" data-testid="user-menu-admin" onClick={() => setUserMenu(false)} className="flex items-center gap-2 px-4 py-3 text-[13px] font-bold-dl hover:bg-[#F6F3EE]">
                        <ShieldCheck size={14} /> {lang === "fr" ? "Backoffice" : "Backoffice"}
                      </Link>
                    )}
                    <button data-testid="user-menu-logout" onClick={doLogout} className="w-full text-left flex items-center gap-2 px-4 py-3 text-[13px] font-bold-dl hover:bg-[#F6F3EE] border-t border-[#E6E0D5]">
                      <LogOut size={14} /> {lang === "fr" ? "Se déconnecter" : "Sign out"}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/login"
                data-testid="header-signin-btn"
                className="hidden md:inline-flex items-center gap-1.5 text-[#0B1210] hover:text-[#00A878] px-2 h-10 text-[13px] font-bold-dl transition-colors"
              >
                <LogIn size={13} /> <span>{lang === "fr" ? "Connexion" : "Sign in"}</span>
              </Link>
            )}

            <Link
              to="/contact"
              data-testid="talk-to-dataleo-btn"
              className="dl-pill dl-pill-primary !py-2.5 !px-5 !min-h-[44px] !text-[13px] whitespace-nowrap"
            >
              <Mail size={14} strokeWidth={2.5} />
              <span className="hidden xs:inline sm:inline">{lang === "fr" ? "Contact" : "Talk"}</span>
            </Link>

            <button
              data-testid="mobile-menu-btn"
              onClick={() => setDrawer(true)}
              aria-label="Open menu"
              className="md:hidden grid place-items-center w-10 h-10 rounded-full text-[#0B1210] hover:bg-[#EFEAE0] transition-colors"
            >
              <Menu size={20} />
            </button>
          </div>
        </div>

        {/* Contextual sub-strip - only on section pages. Quiet underline nav. */}
        {subNav && (
          <div className="bg-white border-t border-[#E6E0D5] relative">
            <div className="dl-container relative">
              <div className="flex items-center gap-5 h-11 overflow-x-auto no-scrollbar" data-testid="sub-nav">
                <span className="font-mono-dl text-[10px] tracking-[0.22em] uppercase shrink-0 text-[#4B5563]">
                  {subNav.label}
                </span>
                {subNav.items.map((n) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    end={n.end}
                    data-testid={`subnav-${n.to.replace(/\//g, "-").replace(/^-/, "")}`}
                    className={({ isActive }) =>
                      `inline-flex items-center gap-1.5 text-[12.5px] font-bold-dl whitespace-nowrap transition-colors h-11 min-h-[40px] ${
                        isActive
                          ? "text-[#0B1210] border-b-2 border-[#00A878]"
                          : "text-[#4B5563] hover:text-[#0B1210]"
                      }`
                    }
                  >
                    {n.leadingIcon}
                    {n.label}
                  </NavLink>
                ))}
              </div>
              <div
                aria-hidden="true"
                className="md:hidden pointer-events-none absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white to-transparent"
              />
            </div>
          </div>
        )}
      </header>

      {/* Mobile drawer */}
      {drawer && (
        <div className="fixed inset-0 z-[60] md:hidden" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setDrawer(false)} aria-hidden />
          <aside className="absolute top-0 right-0 h-full w-[88vw] max-w-sm bg-[#F6F3EE] text-[#0B1210] flex flex-col shadow-2xl">
            <div className="flex items-center justify-between h-[72px] px-5 border-b border-[#E6E0D5] shrink-0">
              <span className="font-black-dl text-[16px] text-[#00A878]">Dataleo</span>
              <button
                onClick={() => setDrawer(false)}
                aria-label="Close menu"
                data-testid="mobile-drawer-close"
                className="w-11 h-11 grid place-items-center rounded-full hover:bg-[#EFEAE0]"
              >
                <X size={22} />
              </button>
            </div>

            <div className="px-5 py-4 border-b border-[#E6E0D5] shrink-0">
              {user ? (
                <Link
                  to="/account"
                  onClick={() => setDrawer(false)}
                  data-testid="mobile-account-link"
                  className="flex items-center gap-3 min-h-[44px] hover:opacity-80"
                >
                  <span className="w-9 h-9 rounded-full bg-[#00A878] text-white grid place-items-center text-[13px] font-bold shrink-0">
                    {(user.name || user.email || "?").slice(0, 1).toUpperCase()}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-[14px] font-bold-dl truncate">{user.name || user.email.split("@")[0]}</div>
                    <div className="text-[#4B5563] text-[11px] truncate">{user.email}</div>
                  </div>
                  <ChevronRight size={16} className="text-[#4B5563] shrink-0" />
                </Link>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  <Link to="/login" onClick={() => setDrawer(false)} data-testid="mobile-signin-btn" className="dl-pill dl-pill-outline !text-[13px] justify-center">
                    <LogIn size={13} /> {lang === "fr" ? "Connexion" : "Sign in"}
                  </Link>
                  <Link to="/register" onClick={() => setDrawer(false)} data-testid="mobile-register-btn" className="dl-pill dl-pill-primary !text-[13px] justify-center">
                    <UserPlus size={13} /> {lang === "fr" ? "S'inscrire" : "Register"}
                  </Link>
                </div>
              )}
            </div>

            <nav className="flex-1 overflow-y-auto py-3">
              {drawerNav.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  data-testid={`mobile-nav-${n.to.replace(/\//g, "-").replace(/^-/, "")}`}
                  className={({ isActive }) =>
                    `flex items-center gap-2 px-6 min-h-[52px] text-[16px] font-bold-dl transition-colors ${
                      n.divider ? "text-[#4B5563] mt-4 pt-4 border-t border-[#E6E0D5]" :
                      isActive ? "text-[#00A878] bg-white" : "text-[#0B1210] hover:bg-white"
                    }`
                  }
                >
                  {n.leadingIcon}
                  {n.label}
                </NavLink>
              ))}
            </nav>

            <div className="px-5 py-4 border-t border-[#E6E0D5] space-y-2 shrink-0">
              <div className="flex items-center gap-2">
                <button data-testid="mobile-lang-fr" onClick={() => switchLang("fr")} className={`flex-1 flex items-center justify-center gap-1 px-3 min-h-[44px] rounded-full border ${lang === "fr" ? "border-[#0B1210] bg-white" : "border-transparent opacity-60"}`}>
                  <FlagFR active={lang === "fr"} /> <span className="text-[12px] font-bold-dl">FR</span>
                </button>
                <button data-testid="mobile-lang-en" onClick={() => switchLang("en")} className={`flex-1 flex items-center justify-center gap-1 px-3 min-h-[44px] rounded-full border ${lang === "en" ? "border-[#0B1210] bg-white" : "border-transparent opacity-60"}`}>
                  <FlagGB active={lang === "en"} /> <span className="text-[12px] font-bold-dl">EN</span>
                </button>
              </div>
              {user && user.role === "admin" && (
                <Link to="/admin" onClick={() => setDrawer(false)} className="dl-pill dl-pill-outline w-full justify-center">
                  <ShieldCheck size={14} /> Backoffice
                </Link>
              )}
              {user && (
                <button data-testid="mobile-logout-btn" onClick={async () => { setDrawer(false); await doLogout(); }} className="w-full flex items-center justify-center gap-2 text-[#4B5563] hover:text-[#0B1210] px-4 min-h-[44px] text-[13px] font-bold-dl">
                  <LogOut size={14} /> {lang === "fr" ? "Se déconnecter" : "Sign out"}
                </button>
              )}
              <Link to="/contact" onClick={() => setDrawer(false)} className="dl-pill dl-pill-primary w-full justify-center">
                <Mail size={14} /> {lang === "fr" ? "Contacter Dataleo" : "Talk to Dataleo"}
                <ArrowRight size={14} strokeWidth={2.5} />
              </Link>
            </div>
          </aside>
        </div>
      )}
    </>
  );
};

export default Header;
